<template>
  <div class="permissao-page">
    <div class="permissao-header sticky-top bg-secondary-theme text-center shadow-sm border-bottom">
      <h1 class="font-weight-bold h6 my-3">Localização para continuar</h1>
    </div>
    <div class="permissao-content">
      <div class="container text-center permissao-container" aria-live="polite" :aria-busy="busy">
        <img src="../assets/icone.png" alt="LeCard" class="mb-4" style="width: 72px">
        <loading v-if="busy" :text="state === 'locating' ? 'Buscando sua localização…' : 'Verificando sua localização…'"/>
        <template v-else>
          <h2 class="h5 font-weight-bold">{{ title }}</h2>
          <template v-if="state === 'prompt'">
            <p>Para organizar as entregas, o LeCard Entregador compartilha sua localização com a empresa enquanto você estiver conectado e disponível.</p>
            <template v-if="nativeTrackingAvailable">
              <p>O rastreamento continua ao usar outro app ou bloquear a tela. O compartilhamento termina ao sair da conta ou remover o app dos recentes.</p>
              <p>Permita a localização exata e as notificações para continuar.</p>
            </template>
            <p v-else>Permita o acesso à localização para continuar. Se houver essa opção, escolha a localização precisa.</p>
          </template>
          <p v-else>{{ message }}</p>
          <div class="permission-actions my-4">
            <button class="btn btn-primary px-4" @click="verify(true)">{{ state === 'prompt' ? 'Permitir localização' : state === 'denied' ? 'Já autorizei, verificar novamente' : 'Tentar novamente' }}</button>
            <button v-if="settingsTarget" class="btn btn-outline-primary px-4" @click="openSettings">Abrir configurações</button>
          </div>
        </template>
        <router-link v-if="!busy && state === 'prompt'" to="/politica-de-privacidade" class="text-link d-inline-block mt-4">Política de Privacidade</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from '@/components/Loading';
import { checkLocationPermission } from '@/services/locationPermission';
import { isNativeTrackingAvailable, requestNativeTrackingPermissions, openNativeTrackingSettings } from '@/services/nativeTracking';

export default {
  name: 'Permissao',
  components: { Loading },
  data() {
    return { state: 'checking', message: '', settingsTarget: null, busy: false, disposed: false,
      verifyPending: false };
  },
  computed: {
    nativeTrackingAvailable() { return isNativeTrackingAvailable(); },
    title() {
      return { prompt: 'Permita sua localização', denied: 'Acesso à localização bloqueado',
        precise: 'Ative a localização exata', notifications: 'Permita as notificações',
        unavailable: 'Localização indisponível', unsupported: 'Localização não disponível neste navegador' }[this.state];
    }
  },
  methods: {
    show(state, message, settingsTarget = null) {
      this.state = state;
      this.message = message;
      this.settingsTarget = settingsTarget;
    },
    async verify(request = false) {
      if (this.busy || this.disposed) return;
      this.verifyPending = false;
      this.busy = true;
      this.show('checking', '');
      try {
        let permission = await checkLocationPermission();
        if (this.disposed) return;
        if (this.nativeTrackingAvailable && request) {
          permission = await requestNativeTrackingPermissions();
          if (this.disposed) return;
        }
        if (permission.approximateLocation) {
          this.show('precise', 'Abra as configurações do aplicativo, toque em Permissões > Localização e ative Usar localização exata.', 'app');
          return;
        }
        if (!permission.location && (this.nativeTrackingAvailable || permission.state !== 'unknown')) {
          if (permission.state === 'unavailable') {
            this.show('unsupported', 'Abra o site em um navegador compatível, usando uma conexão HTTPS.');
            return;
          }
          if (permission.state === 'denied' || (this.nativeTrackingAvailable && request)) {
            this.show('denied', this.nativeTrackingAvailable
              ? 'Autorize a localização nas configurações do aplicativo para continuar.'
              : 'Autorize a localização nas permissões deste site. Confira também se o navegador pode acessar a localização nas configurações do celular.', this.nativeTrackingAvailable ? 'app' : null);
            return;
          }
          if (!request) {
            this.show('prompt', '');
            return;
          }
        }
        // Sem Permissions API, só a tentativa real confirma o acesso.
        if (permission.state === 'unknown' && !request) {
          this.show('prompt', '');
          return;
        }
        if (permission.notifications === false) {
          this.show('notifications', 'Autorize as notificações nas configurações do aplicativo para manter o rastreamento ativo em segundo plano.', 'app');
          return;
        }
        if (permission.locationServices === false) {
          this.show('unavailable', 'A localização do celular está desligada. Ative-a para continuar.', 'location_services');
          return;
        }
        this.state = 'locating';
        const position = await new Promise((resolve, reject) => {
          if (!navigator.geolocation) return reject({ code: 2 });
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true, timeout: 10000, maximumAge: 0
          });
        });
        if (this.disposed) return;
        // Recheck permissions after returning from settings before granting access.
        if (this.verifyPending || document.visibilityState !== 'visible') return;
        if (!Number.isFinite(position.coords.latitude) || !Number.isFinite(position.coords.longitude)) {
          throw { code: 2 };
        }
        localStorage.setItem('geoLocation', 'true');
        this.$emit('ready', position);
      } catch (error) {
        if (this.disposed) return;
        localStorage.removeItem('geoLocation');
        if (error.code === 1) {
          this.show('denied', 'O acesso à localização foi bloqueado. Verifique as permissões do site e do navegador nas configurações do celular.', this.nativeTrackingAvailable ? 'app' : null);
        } else {
          this.show('unavailable', 'Não conseguimos obter sua localização. Verifique se a localização do celular está ativada. Se estiver em um local fechado, tente ir para uma área aberta.', this.nativeTrackingAvailable ? 'location_services' : null);
        }
      } finally {
        if (!this.disposed) {
          this.busy = false;
          if (this.verifyPending && document.visibilityState === 'visible') this.verify();
        }
      }
    },
    onReturn() {
      if (document.visibilityState !== 'visible' || this.disposed) return;
      if (this.busy) {
        this.verifyPending = true;
      } else {
        this.verify();
      }
    },
    async openSettings() {
      try { await openNativeTrackingSettings(this.settingsTarget); }
      catch (error) { this.message = 'Não foi possível abrir as configurações. Abra os ajustes do celular manualmente.'; }
    }
  },
  mounted() {
    document.addEventListener('visibilitychange', this.onReturn);
    this.verify();
  },
  beforeDestroy() {
    this.disposed = true;
    document.removeEventListener('visibilitychange', this.onReturn);
  }
};
</script>

<style scoped>
.permissao-page {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
}

.permissao-header {
  padding-top: var(--safe-area-inset-top, env(safe-area-inset-top, 0));
}

.permissao-content {
  flex: 1 0 auto;
  display: flex;
  align-items: center;
}

.permissao-loading {
  justify-content: center;
  padding: 32px 16px;
}

.permissao-container {
  padding-top: 24px;
  padding-bottom: 48px;
}

.permission-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
}

@media (max-width: 767px) {
  .permissao-container {
    padding-top: 36px;
    padding-bottom: 64px;
  }
}

.ios_platform .permissao-container {
  padding-top: 44px;
  padding-bottom: 72px;
}
</style>
